/**
 * Tour Schedule Modal Component
 * Modal for scheduling a facility tour for a lead
 */

import React, { useState } from 'react';
import { X, Home, Calendar, Clock, Users, FileText } from 'lucide-react';

const TOUR_TYPES = [
  { id: 'facility_tour', label: 'Facility Tour', description: 'General tour of the facility' },
  { id: 'class_observation', label: 'Class Observation', description: 'Observe a class in session' },
  { id: 'meet_teacher', label: 'Meet the Teacher', description: 'Meet with specific teacher' },
  { id: 'trial_class', label: 'Trial Class', description: 'Trial class participation' },
];

const TOUR_DURATIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
];

const TourScheduleModal = ({ isOpen, onClose, onSubmit, isLoading, staff = [] }) => {
  const [formData, setFormData] = useState({
    tourType: 'facility_tour',
    scheduledDate: '',
    scheduledTime: '',
    duration: 30,
    attendees: 1,
    staff: [],
    notes: '',
    sendReminder: true,
  });

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Combine date and time
    const scheduledAt = formData.scheduledDate && formData.scheduledTime
      ? new Date(`${formData.scheduledDate}T${formData.scheduledTime}`).toISOString()
      : null;
    
    const data = {
      tourType: TOUR_TYPES.find(t => t.id === formData.tourType)?.label || formData.tourType,
      scheduledAt,
      duration: parseInt(formData.duration),
      attendees: parseInt(formData.attendees),
      staff: formData.staff,
      notes: formData.notes,
      sendReminder: formData.sendReminder,
    };

    onSubmit(data);
  };

  const handleClose = () => {
    setFormData({
      tourType: 'facility_tour',
      scheduledDate: '',
      scheduledTime: '',
      duration: 30,
      attendees: 1,
      staff: [],
      notes: '',
      sendReminder: true,
    });
    onClose();
  };

  const handleStaffToggle = (staffId) => {
    setFormData(prev => ({
      ...prev,
      staff: prev.staff.includes(staffId)
        ? prev.staff.filter(id => id !== staffId)
        : [...prev.staff, staffId]
    }));
  };

  // Get today's date for min date input
  const today = new Date().toISOString().split('T')[0];

  // eslint-disable-next-line no-unused-vars
  const _selectedTourType = TOUR_TYPES.find(t => t.id === formData.tourType);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
              <Home size={20} className="text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Schedule Tour</h2>
              <p className="text-sm text-gray-500">Schedule a facility tour for this lead</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form id="tour-form" onSubmit={handleSubmit} className="space-y-6">
            {/* Tour Type */}
            <div>
              <label className="form-label">Tour Type *</label>
              <div className="space-y-2 mt-2">
                {TOUR_TYPES.map((type) => (
                  <label
                    key={type.id}
                    className={`flex items-start p-3 rounded-lg border cursor-pointer transition-all ${
                      formData.tourType === type.id
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="tourType"
                      value={type.id}
                      checked={formData.tourType === type.id}
                      onChange={(e) => setFormData({ ...formData, tourType: e.target.value })}
                      className="mt-1 w-4 h-4 text-purple-600"
                    />
                    <div className="ml-3">
                      <span className="font-medium text-gray-900">{type.label}</span>
                      <p className="text-sm text-gray-500">{type.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">
                  <Calendar size={14} className="inline mr-1" />
                  Date *
                </label>
                <input
                  type="date"
                  className="form-input"
                  min={today}
                  value={formData.scheduledDate}
                  onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="form-label">
                  <Clock size={14} className="inline mr-1" />
                  Time *
                </label>
                <input
                  type="time"
                  className="form-input"
                  value={formData.scheduledTime}
                  onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Duration and Attendees */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Duration</label>
                <select
                  className="form-input"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                >
                  {TOUR_DURATIONS.map((duration) => (
                    <option key={duration.value} value={duration.value}>
                      {duration.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">
                  <Users size={14} className="inline mr-1" />
                  Attendees
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  className="form-input"
                  value={formData.attendees}
                  onChange={(e) => setFormData({ ...formData, attendees: e.target.value })}
                />
              </div>
            </div>

            {/* Staff Selection */}
            {staff.length > 0 && (
              <div>
                <label className="form-label">Assign Staff</label>
                <div className="mt-2 space-y-2 max-h-32 overflow-y-auto border rounded-lg p-2">
                  {staff.map((member) => (
                    <label
                      key={member.id}
                      className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.staff.includes(member.id)}
                        onChange={() => handleStaffToggle(member.id)}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                      <span className="text-gray-700">
                        {member.firstName} {member.lastName}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="form-label">
                <FileText size={14} className="inline mr-1" />
                Notes
              </label>
              <textarea
                className="form-input"
                rows="3"
                placeholder="Any special requests or notes..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>

            {/* Reminder toggle */}
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={formData.sendReminder}
                onChange={(e) => setFormData({ ...formData, sendReminder: e.target.checked })}
                className="w-4 h-4 text-purple-600 rounded"
              />
              <span className="text-gray-700">Send reminder email to lead</span>
            </label>
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-100">
          <button
            type="button"
            onClick={handleClose}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="tour-form"
            disabled={isLoading || !formData.scheduledDate || !formData.scheduledTime}
            className="btn-primary disabled:opacity-50 flex items-center space-x-2"
          >
            <Home size={18} />
            <span>{isLoading ? 'Scheduling...' : 'Schedule Tour'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TourScheduleModal;
